import { ChevronDown } from 'lucide-react';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';

/** A practice that specifies a supplier's products, or a product. */
export interface Specifier {
    ID: string | number;
    Title: string;
    URLSegment: string;
    CompanyLogo?: string | null;
    LogoBackgroundColor?: string | null;
    PrimaryCategory?: { PrettyTitle?: string | null } | null;
}

/**
 * The list's frame: four rows (2.5rem each) and the three gaps between them,
 * so four show whole and the rest scroll under a hard edge.
 */
const FRAME = 'h-[11.875rem]';
const SHOWN = 4;

/** The category listed first: the architects who specify the products. */
const FIRST_CATEGORY = 'Architects';

/** Architects first, then everyone else, each in the order given. */
const architectsFirst = (specifiers: Specifier[]) => [
    ...specifiers.filter(
        (specifier) =>
            specifier.PrimaryCategory?.PrettyTitle === FIRST_CATEGORY,
    ),
    ...specifiers.filter(
        (specifier) =>
            specifier.PrimaryCategory?.PrettyTitle !== FIRST_CATEGORY,
    ),
];

/** A practice's mark in a circle, or its initial where it has none. */
const SpecifierMark = ({ specifier }: { specifier: Specifier }) => (
    <span
        className="flex size-[2.25rem] shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/10 font-sans text-[0.875rem] font-medium text-[#323232]"
        style={{ backgroundColor: specifier.LogoBackgroundColor || '#f2f2f2' }}
    >
        {specifier.CompanyLogo ? (
            <img
                src={specifier.CompanyLogo}
                alt=""
                loading="lazy"
                className="size-[72%] object-contain"
            />
        ) : (
            specifier.Title.slice(0, 1)
        )}
    </span>
);

const MORE_LINE =
    'mt-[0.75rem] flex items-center gap-[0.5rem] self-start font-sans text-[0.8125rem] text-[#8a8a8a] no-underline';

/** How many more there are: a link to them all where there is a page of them. */
const MoreLine = ({ to, hidden }: { to?: string; hidden: number }) => {
    const content = (
        <>
            <ChevronDown className="size-[1.125rem]" aria-hidden="true" />
            {`(${hidden}) Professionals`}
        </>
    );
    return to ? (
        <RoccoLink
            to={to}
            className={cn(
                MORE_LINE,
                'transition-colors hover:text-[#181818] hover:no-underline',
            )}
        >
            {content}
        </RoccoLink>
    ) : (
        <span className={MORE_LINE}>{content}</span>
    );
};

/**
 * The professionals who specify a supplier's products, as the logged-in
 * feed lists them: each practice's mark, its name and what it does, the
 * architects first. Four show and the rest scroll in the same frame; under
 * them, how many there are, which opens where they are all listed when
 * there is such a page.
 */
export const SpecifiedByList = ({
    specifiers,
    moreTo,
}: {
    specifiers: Specifier[];
    /** Where every specifier is listed: a profile's Network tab. */
    moreTo?: string;
}) => {
    const ordered = architectsFirst(specifiers);
    const hidden = ordered.length - SHOWN;
    return (
        <div className="flex flex-col">
            <ul
                className={cn(
                    'm-0 flex list-none flex-col gap-[0.625rem] overflow-y-auto overscroll-contain p-0',
                    hidden > 0 && FRAME,
                )}
            >
                {ordered.map((specifier) => (
                    <li key={specifier.ID} className="flex h-[2.5rem] shrink-0">
                        <RoccoLink
                            to={`/professional/${specifier.URLSegment}`}
                            className="flex items-center gap-[0.75rem] hover:opacity-80"
                        >
                            <SpecifierMark specifier={specifier} />
                            <span className="min-w-0">
                                <span className="block truncate font-sans text-[0.9375rem] leading-[1.25] font-medium text-[#181818]">
                                    {specifier.Title}
                                </span>
                                {specifier.PrimaryCategory?.PrettyTitle ? (
                                    <span className="block truncate font-sans text-[0.8125rem] text-[#8a8a8a]">
                                        {specifier.PrimaryCategory.PrettyTitle}
                                    </span>
                                ) : null}
                            </span>
                        </RoccoLink>
                    </li>
                ))}
            </ul>
            {hidden > 0 ? <MoreLine to={moreTo} hidden={hidden} /> : null}
        </div>
    );
};
